import { useMemo } from 'react';
import { useAuthentication } from '@repo/core';
import { useSubscriptions } from './useStripeApi.ts';
import type { Subscription } from './useStripeApi.ts';
import type { ApiError } from '../clients/awsApiClient.ts';

export interface ProductWithPurchaseStatus extends Subscription {
    isPurchased: boolean;
}

export interface UseProductsReturn {
    products: ProductWithPurchaseStatus[];
    sortedProducts: ProductWithPurchaseStatus[];
    isLoading: boolean;
    error: ApiError | null;
    isProductPurchased: (product: Subscription) => boolean;
}

/**
 * Custom hook for loading products and analyzing if the product is purchased or not.
 *
 * This hook:
 * - Fetches all available products from Stripe
 * - Checks user permissions/roles to determine which products are already purchased
 * - Provides sorted products based on metadata index
 * - Returns utility function to check purchase status
 *
 * @returns {UseProductsReturn} Products with purchase status and loading state
 */
export const useProducts = (): UseProductsReturn => {
    const { data: products, isLoading, error } = useSubscriptions();
    const { user } = useAuthentication();

    // Extract user roles from permissions
    const userRoles = useMemo(() => {
        return user?.permissions || [];
    }, [user?.permissions]);

    /**
     * Check if a product is already purchased by the current user
     * based on role/permissions metadata
     */
    const isProductPurchased = useMemo(() => {
        return (product: Subscription): boolean => {
            if (!product.metadata?.base_permission) {
                return false;
            }

            // Parse permissions from metadata
            const neededPermision = product.metadata.base_permission;

            // Check if user has any of the required permissions
            return userRoles.includes(neededPermision);
        };
    }, [userRoles]);

    // Enrich products with purchase status
    const productsWithStatus = useMemo((): ProductWithPurchaseStatus[] => {
        if (!products) return [];

        return products.map(product => ({
            ...product,
            isPurchased: isProductPurchased(product)
        }));
    }, [products, isProductPurchased]);

    // Sort products by metadata.index
    const sortedProducts = useMemo((): ProductWithPurchaseStatus[] => {
        return [...productsWithStatus]
            .filter(p => p.metadata?.index !== undefined)
            .sort((a, b) => {
                const indexA = a.metadata?.index ? parseInt(a.metadata.index, 10) : 999;
                const indexB = b.metadata?.index ? parseInt(b.metadata.index, 10) : 999;
                return indexA - indexB;
            });
    }, [productsWithStatus]);

    return {
        products: productsWithStatus,
        sortedProducts,
        isLoading,
        error: error || null,
        isProductPurchased
    };
};
